import React, { FC, useMemo } from "react";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useGetForecastDataQuery } from "../../../slices/ReportDataSlice";
import { exportDataToExcel } from "../../../utils/ExportData";
import { askTable } from "../ask/reportAsk.utils";
import {
  filterProjected,
  forecastGrid,
  forecastItems,
  ForecastMeasure,
  forecastMonths,
  forecastWeeks,
  gridShade,
  shortDate,
} from "../moreReports.utils";
import { formatHours, peopleOf } from "../reports.utils";
import { scopeUserIds, useReportsAccess } from "../reportsAccess";
import { useViewExtra } from "../saved/SavedViewContext";
import { BarColumns } from "../ui/MoreCharts";
import {
  ChoiceMenu,
  PeopleClientTypeFilters,
  useReportFilters,
} from "../ui/ReportFilters";
import {
  Avatar,
  Card,
  Empty,
  Kpi,
  KpiGrid,
  Legend,
  LoadError,
  Loading,
  Note,
  NUM,
  ReportPage,
  TableBox,
  TD,
  TH,
  useFocusRows,
} from "../ui/ReportUi";
import { useAllWorks } from "../useReportData";

const MONTH_OPTIONS = [2, 3, 4, 6, 9, 12].map((n) => ({
  value: String(n),
  label: `Next ${n} months`,
}));
const MEASURES: { value: ForecastMeasure; label: string }[] = [
  { value: "items", label: "Work items" },
  { value: "hours", label: "Budget hours" },
];
const WEEKS_TABLE = "Work due per week";
const GRID_TABLE = "Work due per team member and month";

/** Busy season forecast (2026-11-05): open work coming due + recurring copies still to come. */
export const ForecastReport: FC = () => {
  const access = useReportsAccess();
  const state = useReportFilters();
  const { filters } = state;
  const [monthsText, setMonths] = useViewExtra(
    "months",
    "4",
    MONTH_OPTIONS,
    "",
  );
  const [measure, setMeasure] = useViewExtra<ForecastMeasure>(
    "count",
    "items",
    MEASURES,
    "Count",
  );
  const months = Number(monthsText) || 4;
  const orgId = getLinkedFirm()?.orgId || "";
  const works = useAllWorks({
    hideGreenStatus: true,
    userIds: scopeUserIds(access, filters.userIds),
    customerIds: filters.clientIds,
    worktypeIds: filters.workTypeIds,
  });
  const forecast = useGetForecastDataQuery({ orgId, months }, { skip: !orgId });
  const projected = useMemo(
    () => filterProjected(forecast.data?.projected ?? [], filters),
    [forecast.data, filters],
  );
  const items = useMemo(
    () => forecastItems(works.data, projected),
    [works.data, projected],
  );
  const weeks = useMemo(
    () => forecastWeeks(items, months, measure),
    [items, months, measure],
  );
  const names = useMemo(() => {
    const map: Record<string, string> = {};
    (forecast.data?.people ?? []).forEach((p) => (map[p.id] = p.name));
    works.data.forEach((w) =>
      peopleOf(w).forEach((p) => p.id && (map[p.id] = p.name)),
    );
    return map;
  }, [forecast.data, works.data]);
  const grid = useMemo(
    () => forecastGrid(items, months, names),
    [items, months, names],
  );
  const cols = forecastMonths(months);
  const isLoading = works.isLoading || forecast.isLoading;
  const created = weeks.reduce((s, w) => s + w.created, 0);
  const coming = weeks.reduce((s, w) => s + w.projected, 0);
  const busiest = weeks.reduce<typeof weeks[number] | null>(
    (b, w) => (!b || w.total > b.total ? w : b),
    null,
  );
  const maxCell = Math.max(
    0,
    ...grid.flatMap((r) => r.cells.map((c) => c.items)),
  );
  const unit = (n: number) =>
    measure === "hours" ? formatHours(n) : String(Math.round(n));
  const v = (x: React.ReactNode) => (isLoading ? "…" : x);
  const heavy = busiest ? busiest.total * 0.8 : 0;

  const shownGrid = useFocusRows(GRID_TABLE, grid, (r) => r.id || "unassigned");
  const { pageRows, pager } = usePagedRows(shownGrid);

  const onExport = () =>
    exportDataToExcel(
      [
        ...weeks.map((w) => ({
          "Week of": shortDate(w.start),
          "Already created": w.created,
          "Recurring copies still to come": w.projected,
          Total: w.total,
        })),
      ],
      "Busy season forecast",
    );

  const ask = useMemo(
    () => ({
      title: "Busy season forecast",
      path: "/reports/forecast",
      description: `Work due per week for the next ${months} months, counted as ${
        measure === "hours" ? "budget hours" : "work items"
      }, including recurring copies not created yet.`,
      kpis: [
        { label: "Already created", value: unit(created) },
        { label: "Recurring copies still to come", value: unit(coming) },
        {
          label: "Busiest week",
          value: busiest
            ? `${shortDate(busiest.start)} (${unit(busiest.total)})`
            : "—",
        },
      ],
      tables: [
        askTable(
          WEEKS_TABLE,
          weeks,
          [
            {
              key: "week",
              label: "Week of",
              kind: "date",
              value: (w) => shortDate(w.start),
            },
            {
              key: "created",
              label: "Already created",
              kind: measure === "hours" ? "hours" : "number",
              value: (w) => w.created,
            },
            {
              key: "projected",
              label: "Recurring copies still to come",
              kind: measure === "hours" ? "hours" : "number",
              value: (w) => w.projected,
            },
            {
              key: "total",
              label: "Total",
              kind: measure === "hours" ? "hours" : "number",
              value: (w) => w.total,
            },
          ],
          (w) => shortDate(w.start),
        ),
        askTable(
          GRID_TABLE,
          grid,
          [
            {
              key: "person",
              label: "Team member",
              kind: "text",
              value: (r) => r.name,
            },
            ...cols.map((c, i) => ({
              key: `m${i}`,
              label: c.label,
              kind: "text" as const,
              value: (r: typeof grid[number]) =>
                `${r.cells[i].items} items · ${Math.round(r.cells[i].hours)} h`,
            })),
          ],
          (r) => r.id || "unassigned",
        ),
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weeks, grid, months, measure, created, coming, busiest],
  );

  return (
    <ReportPage
      title={"Busy season forecast"}
      description={
        "Work coming due over the next months, including the copies recurring work will create, so crunch weeks show before they arrive."
      }
      onExport={weeks.length ? onExport : undefined}
      ask={isLoading ? undefined : ask}
      askSuggestions={[
        "Which week is the busiest?",
        "Who has the most work coming in January?",
        "How much of it is recurring work?",
      ]}
      filters={
        <>
          <ChoiceMenu
            label={"Show"}
            options={MONTH_OPTIONS}
            value={monthsText}
            onChange={setMonths}
          />
          <ChoiceMenu
            label={"Count"}
            options={MEASURES}
            value={measure}
            onChange={setMeasure}
          />
          <PeopleClientTypeFilters state={state} showPeople={access.firmWide} />
        </>
      }>
      {(works.isError || forecast.isError) && <LoadError />}
      <KpiGrid>
        <Kpi
          label={"Already created"}
          value={v(unit(created))}
          note={"Open work due in this time"}
        />
        <Kpi
          label={"Still to come"}
          value={v(unit(coming))}
          note={"Recurring copies not created yet"}
        />
        <Kpi
          label={"Busiest week"}
          value={v(
            busiest && busiest.total > 0 ? shortDate(busiest.start) : "—",
          )}
          note={
            busiest && busiest.total > 0
              ? `${unit(busiest.total)} due that week`
              : undefined
          }
          tone={"amber"}
        />
      </KpiGrid>

      <Card
        title={WEEKS_TABLE}
        right={
          <Legend
            items={[
              { label: "Already created", color: "#6B4EFF" },
              { label: "Recurring copies still to come", color: "#C9BEFF" },
            ]}
          />
        }>
        {isLoading ? (
          <Loading />
        ) : (
          <BarColumns
            stacked
            height={220}
            series={[
              { key: "created", label: "Already created", color: "#6B4EFF" },
              { key: "projected", label: "Still to come", color: "#C9BEFF" },
            ]}
            buckets={weeks.map((w) => ({
              key: w.start.toISOString(),
              label: w.label,
              values: [w.created, w.projected],
              top: w.total
                ? measure === "hours"
                  ? String(Math.round(w.total))
                  : String(w.total)
                : "",
              topTone: w.total > 0 && w.total >= heavy ? "red" : undefined,
            }))}
            format={unit}
          />
        )}
      </Card>

      <Card title={GRID_TABLE}>
        {isLoading ? (
          <Loading />
        ) : !grid.length ? (
          <Empty text={"Nothing due in this time."} />
        ) : (
          <TableBox pager={pager}>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  Team member
                </th>
                {cols.map((c) => (
                  <th key={c.key} scope={"col"} className={TH}>
                    {c.label}
                  </th>
                ))}
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => (
                <tr key={r.id || "unassigned"}>
                  <td className={`${TD} font-bold`}>
                    {r.id ? <Avatar name={r.name} /> : null}
                    {r.name}
                  </td>
                  {r.cells.map((c, i) => {
                    const shade = gridShade(c.items, maxCell);
                    return (
                      <td key={cols[i].key} className={TD}>
                        <span
                          className={
                            "inline-block rounded-lg px-2.5 py-1.5 text-[13px] font-semibold"
                          }
                          style={{
                            backgroundColor: shade.bg,
                            color: shade.fg,
                          }}>
                          {c.items} · {Math.round(c.hours)} h
                        </span>
                      </td>
                    );
                  })}
                  <td className={`${TD} ${NUM} font-extrabold`}>{r.total}</td>
                </tr>
              ))}
            </tbody>
          </TableBox>
        )}
      </Card>

      <Note>
        Darker cells = more work due that month (items · budget hours). Overdue
        work is counted in this week. Recurring copies use each series&apos; own
        schedule; for &quot;On completion&quot; series we assume each item is
        finished on its due date. Work with two people counts for both in the
        table.
        {!access.firmWide && " You see your own work."}
      </Note>
    </ReportPage>
  );
};
